// src/storage/useEssayAttempts.js
//
// Auth-bound hooks for PERSISTED essay attempts (Scope B) -- the cross-device
// history of ungraded draft snapshots and AI-graded essays. Owner client-writes to
// /users/{uid}/essayAttempts/{attemptId} (auto-id, immutable snapshots),
// mirroring useProgress / useUserContent (onSnapshot, serverTimestamp(), no-op when
// signed-out / mock / no-db). Attempts are IMMUTABLE (create + delete only; rules
// deny update). takenAt MUST use serverTimestamp() (rules require == request.time).
//
// ADDITIVE to the ephemeral in-session feedback: the writer ALSO persists on a
// successful grade; graded-save errors remain best-effort. Draft saves instead
// reject visibly and must be acknowledged before either AI entrance can run.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc, onSnapshot, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { buildAttemptDoc, buildOwnAttemptDoc } from './essayAttemptHelpers.js';
import { createDraftSaver } from './essayDrafts.js';
import { observeBetaOperation } from '../analytics/betaOperations.js';

const disabled = () => AUTH_MODE === 'mock' || !db;

// Best-effort writer: persists ONE attempt on a successful AI grade. Signed-in +
// entitled only; a graded result only (failures/unavailable never persist). All
// errors are swallowed -- a failed write must NOT disrupt the feedback render.
export function useEssayAttemptWriter() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const writeDraft = useMemo(() => createDraftSaver((owner, body) => observeBetaOperation('draft_save', body, async () => {
        if (disabled() || owner !== uid || auth?.currentUser?.uid !== owner) throw new Error('Sign in to save your draft.');
        const saved = await addDoc(collection(db, 'users', owner, 'essayAttempts'), {
            ...body, takenAt: serverTimestamp(),
        });
        if (auth?.currentUser?.uid !== owner) throw new Error('Account changed during saving.');
        return saved.id; // server acknowledgement, never a pretend best-effort save
    })), [uid]);
    const saveDraft = useCallback(input => writeDraft(uid, input), [uid, writeDraft]);

    const saveAttempt = useCallback(async ({ packId, promptId, promptTitle, essayText, graded, entitled }) => {
        if (disabled() || !uid || !entitled) return;
        const body = buildAttemptDoc({ packId, promptId, promptTitle, essayText, graded });
        if (!body) return; // not a gradeable success -> no-op
        try {
            await addDoc(collection(db, 'users', uid, 'essayAttempts'), {
                ...body, takenAt: serverTimestamp(),
            });
        } catch (e) {
            console.error('saveEssayAttempt write error:', e); // best-effort: swallow
        }
    }, [uid]);

    // Own-essay counterpart (own-essay-ai-grading slice): persists a successful
    // gradeOwnEssay result with the widened optional fields (source:'own',
    // promptText, rubricPackId, packLabel). Same best-effort semantics.
    const saveOwnAttempt = useCallback(async ({ rubricPackId, packLabel, promptText, essayText, graded }) => {
        if (disabled() || !uid) return;
        const body = buildOwnAttemptDoc({ rubricPackId, packLabel, promptText, essayText, graded });
        if (!body) return;
        try {
            await addDoc(collection(db, 'users', uid, 'essayAttempts'), {
                ...body, takenAt: serverTimestamp(),
            });
        } catch (e) {
            console.error('saveOwnEssayAttempt write error:', e); // best-effort: swallow
        }
    }, [uid]);

    return { saveAttempt, saveOwnAttempt, saveDraft, canSaveDraft: !!uid && !disabled() };
}

// Live list of the signed-in user's essay attempts (for the My Essays panel) + a
// per-attempt delete (user-deletable). mock / signed-out / no-db -> empty, not loading.
export function useEssayAttempts() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const [owned, setOwned] = useState({ uid: null, attempts: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (disabled() || !uid) { setOwned({ uid: null, attempts: [] }); setLoading(false); return undefined; }
        setLoading(true);
        let active = true;
        const ref = collection(db, 'users', uid, 'essayAttempts');
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                if (!active) return;
                setOwned({ uid, attempts: list });
                setLoading(false);
            },
            (err) => { if (active) { console.error('useEssayAttempts listener error:', err); setOwned({ uid, attempts: [] }); setLoading(false); } },
        );
        return () => { active = false; unsub(); };
    }, [uid]);

    const deleteAttempt = useCallback(async (id) => {
        if (disabled() || !uid || !id) return;
        try {
            await deleteDoc(doc(db, 'users', uid, 'essayAttempts', id));
        } catch (e) {
            console.error('deleteEssayAttempt error:', e);
            throw e; // surface to the panel so it can show a failed-delete state
        }
    }, [uid]);

    return { attempts: owned.uid === uid ? owned.attempts : [], loading, deleteAttempt };
}
